/**
 * The phase band's two controls, worded: the "+ Faza" menu and the settings popover over the phase
 * in view.
 *
 * ── There is no canon for either, and that is stated rather than glossed ─────────────────────
 * No 01.08 or 04.08 specimen draws a phase editor at all — the band's "+ Faza" is a dashed ghost and
 * the tabs carry no controls. What the canon DOES give is a vocabulary the two are built out of
 * without inventing anything: the `MenuSurface` popup the card's ⋯ already wears, the ± stepper the
 * tape's rest column already draws, and the `ConfirmDialog` the save's structural consent already
 * uses. Every refusal is drawn as words under the control it refuses, which is the same treatment
 * "+ Faza" itself has carried since 4 August.
 *
 * ── The tabs stay tabs ───────────────────────────────────────────────────────────────────────
 * A `tablist` may own only tabs (`aria-required-children`, and a band that announced as four items
 * holding three tabs), so the editor is ONE control beside the strip acting on the phase in view —
 * not a ⋯ per tab. That is also why it is worded here for a single phase: there is one editor and it
 * always edits the tab the canvas below is showing.
 *
 * **A component below this reads only the view model.** No `t`, no arithmetic, no `draft.phases[i]`.
 */

import {
  COLLECTION_CAPS,
  MAX_TOTAL_CYCLES,
  TEXT_LIMITS,
  addPhaseRefusal,
  phaseEditGates,
  phaseWindowShifts,
  type PhaseCycleBounds,
  type PhaseEdit,
  type PhaseEditRefusal,
  type PhaseSummary,
  type PhaseType,
  type PlanDraft,
} from '~/entities/plan-draft';

import { KEY, unitNoun, unitNounLong, type Translate } from './builderCopy';

/** One end of the length stepper — the value it would write, and why it cannot. */
export interface PhaseStepView {
  readonly label: string;
  /** The cycle count this press would set. */
  readonly value: number;
  readonly disabledReason: string | null;
}

/**
 * The phase's length, as the popover draws it: a value and two steps, each carrying its OWN reason.
 *
 * There is no summary line and there deliberately is not: the two ends are refused by different
 * facts — the client's trained cycles hold the floor, the plan-wide cap holds the ceiling — and one
 * sentence for both meant the ⊕ was described by the sentence about shortening. A trainer whose
 * phase could still grow read "krócej się nie da" under a live control.
 */
export interface PhaseLengthView {
  readonly label: string;
  /** "4 tygodnie" / "3 cykle" — declined on the count, which is why it is a counted key. */
  readonly value: string;
  readonly decrease: PhaseStepView;
  readonly increase: PhaseStepView;
}

export interface PhaseTypeOptionView {
  readonly key: PhaseType;
  readonly label: string;
  readonly isSelected: boolean;
}

/** Everything the settings popover renders for the phase in view. */
export interface PhaseEditorView {
  /** "Ustawienia fazy Fundament" — the trigger's accessible name, so it is never a bare glyph. */
  readonly triggerLabel: string;
  readonly title: string;
  readonly nameLabel: string;
  /** The name as AUTHORED, which is what an editor has to open on. */
  readonly name: string;
  readonly nameMaxLength: number;
  readonly typeLabel: string;
  readonly types: readonly PhaseTypeOptionView[];
  readonly length: PhaseLengthView;
  readonly duplicateLabel: string;
  readonly duplicateRefusal: string | null;
  /** The name the copy is minted with — DATA, resolved here because `model/` holds no sentences. */
  readonly duplicateName: string;
  readonly deleteLabel: string;
  /**
   * The delete's OWN cause, and only that — "a plan must keep one phase". `null` otherwise, including
   * when the phase is frozen: that cause is `lockedReason`, which the delete shares with the type.
   */
  readonly deleteRefusal: string | null;
  /**
   * What the client's own history freezes on this phase — its type and its deletion, together, in
   * ONE sentence. `null` when nothing is frozen.
   *
   * They are one fact (`phaseConsumed`, from one predicate), and printing it under the type control
   * and again under Delete made a 288px panel a hundred pixels taller and read as a stutter. Both
   * controls point their `aria-describedby` at this one note, which is also the honest shape: a
   * screen reader announcing the same sentence twice per panel is the same defect one channel over.
   */
  readonly lockedReason: string | null;
  /** The confirm the delete raises. Worded here so the dialog holds no `t` of its own. */
  readonly deletePrompt: PhaseActionPrompt;
}

/** The strings the phase-delete confirm needs. The screen assembles the `ConfirmPrompt`. */
export interface PhaseActionPrompt {
  /**
   * The phase's own name, as DATA rather than as a sentence.
   *
   * The confirm never draws it on its own — `title` already interpolates it — but the live region
   * that announces the deletion afterwards has to name what went, and by then the phase is out of
   * the tree. Captured with the rest of the prompt, at the moment the trainer asked.
   */
  readonly name: string;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
}

/** One phase as a duplication target in the "+ Faza" menu. */
export interface PhaseDuplicateTargetView {
  readonly index: number;
  readonly label: string;
  readonly disabledReason: string | null;
  /** The name the copy is minted with. */
  readonly copyName: string;
}

/** The "+ Faza" menu: two empty phases, then every existing phase as a copy. */
export interface PhaseAddMenuView {
  /** "+ Faza" — the trigger's visible label, unchanged from the canon's ghost. */
  readonly triggerLabel: string;
  /** "Dodaj fazę" — the popup's own accessible name. */
  readonly menuLabel: string;
  readonly addGroup: string;
  readonly buildLabel: string;
  readonly deloadLabel: string;
  /** Why no new phase may be added at all. Applied to both entries. */
  readonly addRefusal: string | null;
  /** The name a fresh phase is minted with — "Faza 4". */
  readonly newName: string;
  readonly duplicateGroup: string;
  readonly targets: readonly PhaseDuplicateTargetView[];
}

/**
 * A refusal code → the sentence a trainer reads, or `null` when there is nothing to refuse.
 *
 * ── The ceiling arrives already DECLINED, as one phrase ──────────────────────────────────────
 * The two cap sentences used to interpolate a bare number and the unit noun's nominative singular,
 * which printed "52 tydzień" — Polish declines the noun on the count and "52" takes the genitive
 * plural. `limit` is therefore `lengthWords`, the same counted key the length stepper already reads,
 * so the sentences say "52 tygodni" and "52 cykli" without either of them knowing how. Execution
 * ADR §7's rule still holds: the unit is interpolated rather than baked into a key name.
 *
 * `minCycles` keeps `{{unit}}` — "co najmniej jeden tydzień" takes the nominative and is correct.
 * The ceilings come from the entity's own tables; a number restated here would be a second answer
 * that drifts the first time the wire moves.
 */
function words(
  t: Translate,
  draft: PlanDraft,
  refusal: PhaseEditRefusal | 'minCycles' | 'maxCycles' | null,
): string | null {
  if (refusal === null) return null;
  const cap =
    refusal === 'maxCycles' ? COLLECTION_CAPS.cyclesPerPhase.max : MAX_TOTAL_CYCLES;
  return t(`${KEY}.phase.refusal.${refusal}`, {
    max: COLLECTION_CAPS.phasesPerPlan.max,
    limit: lengthWords(t, draft, cap),
    unit: unitNounLong(t, draft),
  });
}

/** "4 tygodnie" / "3 cykle" — the counted key, because Polish declines the noun on the count. */
function lengthWords(t: Translate, draft: PlanDraft, cycles: number): string {
  return t(`${KEY}.${draft.mode === 'selfpaced' ? 'cycles' : 'weeks'}`, { count: cycles });
}

function toLength(
  t: Translate,
  draft: PlanDraft,
  cycles: number,
  bounds: PhaseCycleBounds,
): PhaseLengthView {
  // A bound that carries no live-plan cause is still a bound, and it still owes the trainer a
  // sentence: at one cycle the phase simply cannot get shorter, at fifty-two it cannot get longer.
  // Falling back to those rather than to an empty string is what stops a dimmed − from being the
  // one control on this screen that explains nothing.
  const floor = cycles <= bounds.min ? words(t, draft, bounds.floorReason ?? 'minCycles') : null;
  const ceiling =
    cycles >= bounds.max ? words(t, draft, bounds.ceilingReason ?? 'maxCycles') : null;
  return {
    label: t(`${KEY}.phase.lengthLabel`),
    value: lengthWords(t, draft, cycles),
    decrease: { label: t(`${KEY}.phase.shorten`), value: cycles - 1, disabledReason: floor },
    increase: { label: t(`${KEY}.phase.lengthen`), value: cycles + 1, disabledReason: ceiling },
  };
}

/**
 * The settings popover for one phase, or `null` when the index names no phase.
 *
 * `phase` is the DRAFT node rather than the summary, because the editor opens on the name as
 * AUTHORED — a summary carries whatever a projection decided to display, and an editor starting from
 * a displayed fallback would offer to save the placeholder on the first keystroke.
 */
export function toPhaseEditor(
  t: Translate,
  draft: PlanDraft,
  phaseIndex: number,
  isFork: boolean,
): PhaseEditorView | null {
  const phase = draft.phases[phaseIndex];
  const gates = phaseEditGates(draft, phaseIndex);
  if (phase === undefined || gates === null) return null;

  return {
    triggerLabel: t(`${KEY}.phase.editTrigger`, { name: phase.name }),
    title: t(`${KEY}.phase.editTitle`),
    nameLabel: t(`${KEY}.phase.nameLabel`),
    name: phase.name,
    nameMaxLength: TEXT_LIMITS.phaseName.max,
    typeLabel: t(`${KEY}.phase.typeLabel`),
    types: (['build', 'deload'] as const).map((key) => ({
      key,
      label: t(`${KEY}.phaseType.${key}`),
      isSelected: phase.type === key,
    })),
    length: toLength(t, draft, phase.cycles, gates.cycles),
    duplicateLabel: t(`${KEY}.phase.duplicate`),
    duplicateRefusal: words(t, draft, gates.duplication),
    duplicateName: t(`${KEY}.phase.copyName`, { name: phase.name }),
    deleteLabel: t(`${KEY}.phase.delete`),
    // `lastPhase` alone. The frozen-phase cause is `lockedReason` below, shared with the type — and
    // the two CAN both apply, on a one-phase plan the client has trained into, where they say
    // genuinely different things and both are drawn.
    deleteRefusal: gates.removal === 'lastPhase' ? words(t, draft, 'lastPhase') : null,
    lockedReason: words(t, draft, gates.retype),
    deletePrompt: {
      name: phase.name,
      title: t(`${KEY}.phase.deleteTitle`, { name: phase.name }),
      // A running fork's phase disappears from the client's upcoming cycles; a template's simply
      // goes. Neither wording claims the deletion is permanent, because on this app it is not —
      // Cmd+Z restores the phase, its days and their slot tokens until the plan is saved.
      description: t(`${KEY}.phase.${isFork ? 'deleteBodyLive' : 'deleteBody'}`),
      confirmLabel: t(`${KEY}.phase.deleteConfirm`),
      cancelLabel: t(`${KEY}.phase.cancel`),
    },
  };
}

/**
 * The confirm a phase edit owes BEFORE it runs, or `null` when it costs the client nothing.
 *
 * ── Why an act-time confirm as well as the save-time one ─────────────────────────────────────
 * `structuralSaveRoute` still asks at the save, and the two are different questions. The save's is
 * asked once, at the end, about the whole tree, with the server's own count where the server can
 * produce one — and by then the trainer has made several edits and cannot tell which of them moved
 * what. This one is asked at the press and names the block: "«Deload» przesunie się z tyg. 5 na
 * tyg. 9". Neither replaces the other, and the acts that reach both are exactly the ones that move a
 * stored phase.
 *
 * `null` on a library template (`phaseWindowShifts` answers nothing there), on an append at the tail,
 * and on any edit whose gate refuses it — a dialog in front of an act that changes nothing is a
 * confirmation the trainer learns to dismiss.
 *
 * The count is worded off the FIRST shift plus a counted remainder rather than listing eleven phases:
 * the first one is the block the trainer is about to move and the rest are its consequence.
 */
export function toPhaseShiftPrompt(
  t: Translate,
  draft: PlanDraft,
  edit: PhaseEdit,
): PhaseActionPrompt | null {
  const shifts = phaseWindowShifts(draft, edit);
  const first = shifts[0];
  if (first === undefined) return null;

  const unit = unitNoun(t, draft);
  const description = [
    t(`${KEY}.phase.shiftMove`, {
      name: first.name,
      from: `${unit} ${String(first.fromStartCycle)}`,
      to: `${unit} ${String(first.toStartCycle)}`,
    }),
    shifts.length > 1 ? t(`${KEY}.phase.shiftMore`, { count: shifts.length - 1 }) : null,
    t(`${KEY}.phase.shiftTail`),
  ]
    .filter((sentence): sentence is string => sentence !== null)
    .join(' ');

  return {
    name: first.name,
    title: t(`${KEY}.phase.shiftTitle`),
    description,
    confirmLabel: t(`${KEY}.phase.shiftConfirm`),
    cancelLabel: t(`${KEY}.phase.cancel`),
  };
}

export function toPhaseAddMenu(
  t: Translate,
  draft: PlanDraft,
  phases: readonly PhaseSummary[],
): PhaseAddMenuView {
  const unit = unitNoun(t, draft);
  return {
    triggerLabel: t(`${KEY}.addPhase`),
    menuLabel: t(`${KEY}.phase.menuLabel`),
    addGroup: t(`${KEY}.phase.addGroup`),
    buildLabel: t(`${KEY}.phase.addBuild`),
    deloadLabel: t(`${KEY}.phase.addDeload`),
    addRefusal: words(t, draft, addPhaseRefusal(draft)),
    newName: t(`${KEY}.phase.defaultName`, { number: draft.phases.length + 1 }),
    duplicateGroup: t(`${KEY}.phase.duplicateGroup`),
    targets: phases.map((phase) => ({
      index: phase.index,
      // The phase's own name AND its span, so twelve entries are told apart by more than a word the
      // trainer may have used twice.
      label: t(`${KEY}.phase.targetLabel`, {
        name: phase.name,
        range: t(`${KEY}.phaseRange`, {
          unit,
          from: phase.startCycle,
          to: phase.startCycle + phase.cycleCount - 1,
        }),
      }),
      disabledReason: words(t, draft, phaseEditGates(draft, phase.index)?.duplication ?? null),
      copyName: t(`${KEY}.phase.copyName`, { name: phase.name }),
    })),
  };
}
